// Patient detail: header (demographics + latest vitals + risk chips + score
// bars) and the four clinical tabs. Latest vitals + assessment are fetched to
// fill the header; NursingAssessmentDialog files a new assessment snapshot.
import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Paper, Grid, Typography, Button, Chip, Stack, Tabs, Tab, Divider, Avatar, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { useNurseData, applyClinical } from "../NurseDataContext";
import { NURSE_COLORS, initials } from "../theme";
import { VitalsPanel, RiskChips } from "../components";
import { VitalsTab, TasksTab, NotesTab } from "./PatientTabs";
import MedicationsTab from "./MedicationsTab";
import { getAssessment, getVitals, getCarePlanTasks, getCarePlans, listOf } from "../api/nurseApi";
import NursingAssessmentDialog from "../components/NursingAssessmentDialog";
import ScoreBar from "../components/ScoreBar";
import {
  BRADEN_MIN, BRADEN_MAX, MORSE_MIN, MORSE_MAX,
  BRADEN_BANDS, MORSE_BANDS, PAIN_BANDS,
} from "../clinicalScores";
import { fetchDischargeProcess, listDischargeProcesses, STAGE_LABELS, MANNER_OF_DEATH_LABELS } from "../../ipd/api/dischargeProcessApi";
import DischargeModal from "../../ipd/components/modals/DischargeModal";
import DischargeStageTracker from "../../ipd/components/DischargeStageTracker";
import { viewFinalizedSummary } from "../../ipd/utils/viewFinalizedSummary";
import NurseOrderModal from "../components/NurseOrderModal";
import ReturnMedsDialog from "../components/ReturnMedsDialog";
import DoctorInstructionsPanel, { noteTimestamp } from "../components/DoctorInstructionsPanel";
import { formatIstDateTime } from "../../../utils/istDateTime";

// Mirrors the backend's care-plan dispatch (manage_care_plan.py:99-100,
// template_id == 7 -> handle_discharge_advice). Deliberately NOT keyed on
// the CarePlans.name label, which is tenant-editable free text.
const DISCHARGE_ADVICE_TEMPLATE_ID = 7;
// The doctor's written notes, surfaced read-only to the nurse (E9 D9). TWO
// care plans carry them and the nurse needs both — filtering to 8 alone hid
// the doctor's day-to-day notes entirely, which are the ones that change:
//   8 "Clinical Notes" — the admission clerking (history, co-morbidities,
//                        diagnosis). Written once, rarely updated.
//   2 "Note"           — the running note (category, reason, notes). This is
//                        what a doctor writes on a ward round.
const CLINICAL_NOTES_TEMPLATE_ID = 8;
const NOTE_TEMPLATE_ID = 2;
const DOCTOR_NOTE_TEMPLATE_IDS = [NOTE_TEMPLATE_ID, CLINICAL_NOTES_TEMPLATE_ID];

function fmtDateTime(ts) {
  return formatIstDateTime(ts, "—");
}

// Read-only death record (nurse view). Authoring stays with the doctor /
// registrar (the IPD Death Register); the assigned nurse only sees the
// certified facts for a deceased patient. Fields come straight off the
// already-loaded DischargeProcess row (outcome === "deceased") — no extra fetch.
function DeathRecordPanel({ process }) {
  const rows = [
    ["Time of death", fmtDateTime(process.death_datetime)],
    ["Cause of death", process.cause_of_death || "—"],
    ["Manner of death", MANNER_OF_DEATH_LABELS[process.manner_of_death] || "—"],
    ["Certifying doctor", process.certifying_doctor_name || "—"],
    ["Death certificate no.", process.death_certificate_number || "Pending"],
  ];
  return (
    <Paper sx={{ p: 2, borderRadius: 2, mb: 2, border: "1px solid", borderColor: "error.light", bgcolor: "rgba(211,47,47,0.04)" }}>
      <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1, color: "error.main" }}>
        Death Record
        <Chip size="small" color="error" variant="outlined" label="Deceased" />
      </Typography>
      <Grid container spacing={1.5}>
        {rows.map(([label, value]) => (
          <Grid item xs={12} sm={6} md={4} key={label}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Recorded by the certifying doctor. View only.
      </Typography>
    </Paper>
  );
}

export default function PatientDetail() {
  const { patients, selectedAdmissionId, navigate, dateFrom, dateTo } = useNurseData();
  const base = patients.find((p) => p.id === String(selectedAdmissionId));
  const [patient, setPatient] = useState(base);
  const [tab, setTab] = useState(0);
  const [assessmentDialog, setAssessmentDialog] = useState(false);
  // The whole latest assessment row, not just the risk fields — the dialog
  // pre-fills every snapshot from it (ADR 0010 carry-forward) and the score
  // bars read its Braden/Morse totals.
  const [latestAssessment, setLatestAssessment] = useState(null);

  // Discharge status reflects the real DischargeProcess row, which the
  // doctor's Discharge Advice submission (Ward stage) creates and only a
  // doctor can advance further. The nurse's only writable discharge control
  // here is "Initiate Discharge" below, gated on that advice existing (C20-3).
  const [dischargeProcess, setDischargeProcess] = useState(undefined); // undefined = loading, null = none active
  const [cancelledDischarge, setCancelledDischarge] = useState(null);

  // Gate for the "Initiate Discharge" button: true once the doctor has
  // filed a "Discharge Advice" care plan for this admission. undefined
  // while loading, so the button doesn't flash enabled.
  const [hasDischargeAdvice, setHasDischargeAdvice] = useState(undefined);
  const [dischargeModalOpen, setDischargeModalOpen] = useState(false);
  const [dischargeTrackerOpen, setDischargeTrackerOpen] = useState(false);

  // Nurse-placed orders (E10): file Medication / Test care plans via
  // submit_care_plan/. Modal fetches its own fileable-plans list per open.
  const [placeOrderOpen, setPlaceOrderOpen] = useState(false);
  const [returnMedsOpen, setReturnMedsOpen] = useState(false);

  // Doctor Instructions (E9 D9): the doctor's clinical_notes care plans,
  // read-only. undefined = loading, [] = none, [...] = entries (newest first).
  const [doctorInstructions, setDoctorInstructions] = useState(undefined);

  const admissionId = base?.admissionId;
  const patientId = base?.patientId;

  const loadClinical = useCallback(() => {
    if (!admissionId) return;
    Promise.all([
      getAssessment(admissionId, { latest: 1 }).then((r) => r?.data || null).catch(() => null),
      getVitals(admissionId, { limit: 1 }).then((r) => listOf(r)[0] || null).catch(() => null),
    ]).then(([assessment, latestVital]) => {
      setPatient((prev) => applyClinical(prev || base, assessment, latestVital));
      setLatestAssessment(assessment);
    });
  }, [admissionId, base]);

  const loadDischargeProcess = useCallback(() => {
    if (!admissionId) return;
    setDischargeProcess(undefined);
    setCancelledDischarge(null);
    fetchDischargeProcess(admissionId)
      .then(async (res) => {
        const proc = res?.data || null;
        setDischargeProcess(proc);
        if (!proc || proc.stage === "cancelled") {
          try {
            const cancelledList = await listDischargeProcesses({ stage: "cancelled" });
            const found = (cancelledList || []).find(
              (p) => Number(p.admission_id) === Number(admissionId)
            );
            if (found) {
              setCancelledDischarge(found);
            }
          } catch {
            // non-fatal
          }
        }
      })
      .catch(async () => {
        setDischargeProcess(null);
        try {
          const cancelledList = await listDischargeProcesses({ stage: "cancelled" });
          const found = (cancelledList || []).find(
            (p) => Number(p.admission_id) === Number(admissionId)
          );
          if (found) {
            setCancelledDischarge(found);
          }
        } catch {
          // non-fatal
        }
      });
  }, [admissionId]);

  // Reuses the same care_plan_tasks/ fetch (getCarePlanTasks + listOf from
  // nurseApi.js) that TasksTab already calls — no role gate on that
  // endpoint, so it's nurse-callable. Unfiltered by date: we need to know
  // if advice was EVER filed, not just within the dashboard's date range.
  const loadDischargeAdvice = useCallback(() => {
    if (!admissionId) return;
    setHasDischargeAdvice(undefined);
    getCarePlanTasks({ admission_id: admissionId })
      .then((res) => setHasDischargeAdvice(listOf(res).some((t) => Number(t?.template_id) === DISCHARGE_ADVICE_TEMPLATE_ID)))
      .catch(() => setHasDischargeAdvice(false));
  }, [admissionId]);

  // Fetches the doctor's care plans via the identity-gated GET /care_plans/
  // (nurse-readable per E9-BE) and keeps only clinical_notes (template 8).
  // Flat envelope — check .status before reading .data. Resets to loading at
  // the top so one patient's instructions never linger on the next.
  const loadDoctorInstructions = useCallback(() => {
    if (!admissionId) { setDoctorInstructions([]); return; }
    setDoctorInstructions(undefined);
    getCarePlans(admissionId)
      .then((res) => {
        if (res?.status !== "Success") { setDoctorInstructions([]); return; }
        const notes = (res.data || [])
          .filter((r) => DOCTOR_NOTE_TEMPLATE_IDS.includes(Number(r?.template_id)))
          .sort((a, b) => noteTimestamp(b) - noteTimestamp(a));
        setDoctorInstructions(notes);
      })
      .catch(() => setDoctorInstructions([]));
  }, [admissionId]);

  const closeDischargeModal = () => {
    setDischargeModalOpen(false);
    // DischargeModal has no success callback — it just calls onClose on both
    // a successful initiate and a plain cancel (C11). Re-running
    // loadDischargeProcess() here re-fetches either way: after a real
    // success it returns the new Ward-stage row (chip flips, button
    // disappears); after a cancel it re-fetches the same null (harmless).
    loadDischargeProcess();
  };

  useEffect(() => { setPatient(base); }, [base]);
  useEffect(() => { loadClinical(); }, [loadClinical]);
  useEffect(() => { loadDischargeProcess(); }, [loadDischargeProcess]);
  useEffect(() => { loadDischargeAdvice(); }, [loadDischargeAdvice]);
  useEffect(() => { loadDoctorInstructions(); }, [loadDoctorInstructions]);
  // Poll discharge process every 25s so a doctor's cancellation or advance
  // reflects on the nurse dashboard without requiring a manual reload.
  useEffect(() => {
    if (!admissionId) return undefined;
    const interval = setInterval(() => {
      loadDischargeProcess();
    }, 25000);
    return () => clearInterval(interval);
  }, [admissionId, loadDischargeProcess]);
  // Close the Place Order modal if the selected patient changes, so an order is
  // never filed against the wrong admission.
  useEffect(() => { setPlaceOrderOpen(false); }, [admissionId]);

  if (!base) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography>Patient not found.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate("patients")}>Back to patients</Button>
      </Paper>
    );
  }

  const effectiveCancellation =
    dischargeProcess?.stage === "cancelled" ? dischargeProcess : cancelledDischarge;
  const isDischargeCancelled = Boolean(effectiveCancellation);

  const dischargeStageLabel = isDischargeCancelled
    ? "Cancelled"
    : dischargeProcess && dischargeProcess.stage
      ? STAGE_LABELS[dischargeProcess.stage] || dischargeProcess.stage
      : "Not initiated";

  return (
    <Box>
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ alignItems: { md: "center" } }}>
          <Tooltip title="Back"><IconButton onClick={() => navigate("patients")}><ArrowBackIcon /></IconButton></Tooltip>
          <Avatar sx={{ bgcolor: NURSE_COLORS.primary, width: 56, height: 56 }}>{initials(patient?.name)}</Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }} useFlexGap>
              <Typography variant="h6">{patient?.name}</Typography>
              {patient?.age != null && <Chip size="small" label={`${patient.age}${patient.gender || ""}`} />}
              <Chip size="small" label={patient?.uhid} variant="outlined" />
              <Chip size="small" icon={<LocalHospitalIcon sx={{ fontSize: 14 }} />} label={[patient?.wardName || patient?.ward, patient?.bed].filter(Boolean).join(" · ") || "Bed —"} color="primary" variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {[patient?.diagnosis && `Dx: ${patient.diagnosis}`, patient?.consultant && `Consultant: ${patient.consultant}`, patient?.admissionDate && `Admitted: ${patient.admissionDate}`].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}><VitalsPanel vitals={patient?.vitals || {}} /></Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
              <RiskChips patient={patient || {}} />
              {/* Score bars sit under the chips: the chip says which band, the
                  bar says how far into it — a Morse 26 and a Morse 44 are both
                  "Moderate" and need different attention. */}
              <Box sx={{ width: "100%" }}>
                <Stack spacing={1}>
                  <ScoreBar
                    label="Braden (lower = higher risk)"
                    score={latestAssessment?.braden?.total ?? null}
                    min={BRADEN_MIN} max={BRADEN_MAX} bands={BRADEN_BANDS}
                    level={patient?.pressureUlcerRisk}
                    hint="Pressure ulcer risk"
                  />
                  <ScoreBar
                    label="Morse (higher = higher risk)"
                    score={latestAssessment?.morse?.total ?? null}
                    min={MORSE_MIN} max={MORSE_MAX} bands={MORSE_BANDS}
                    level={patient?.fallRisk}
                    hint="Fall risk"
                  />
                  <ScoreBar
                    label="Pain"
                    score={patient?.painScore ?? null}
                    min={0} max={10} bands={PAIN_BANDS}
                    level={PAIN_BANDS.find((b) => (patient?.painScore ?? 99) <= b.upTo)?.level}
                  />
                </Stack>
              </Box>
              <Button size="small" startIcon={<EditIcon />} onClick={() => setAssessmentDialog(true)}>Assessment</Button>
              <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => setPlaceOrderOpen(true)}>Place Order</Button>
              <Button size="small" onClick={() => setReturnMedsOpen(true)}>Return Unused Meds</Button>
              {/* The nurse physically hands this document to the patient and
                  had no way to see it anywhere in the dashboard. */}
              <Button size="small" onClick={() => viewFinalizedSummary(admissionId)}>
                Discharge Summary
              </Button>
              {dischargeProcess === undefined ? null : (
                // Clickable once a process exists — opens the DischargeStageTracker.
                // At most stages this is a status view (the discharge is with the
                // doctor or the billing desk). At nursing_discharge it is the
                // nurse's own step: the tracker then shows the checklist and the
                // Mark Physically Discharged button.
                <Tooltip title={dischargeProcess ? "View discharge progress" : "Discharge not initiated"}>
                  <Chip
                    size="small"
                    icon={<ExitToAppIcon sx={{ fontSize: 16 }} />}
                    label={`Discharge: ${dischargeStageLabel}`}
                    color={dischargeStageLabel === "Not initiated" ? "default" : "primary"}
                    variant="outlined"
                    clickable={Boolean(dischargeProcess)}
                    onClick={dischargeProcess ? () => setDischargeTrackerOpen(true) : undefined}
                  />
                </Tooltip>
              )}
              {dischargeProcess?.stage === "nursing_discharge" && (
                // The discharge is waiting on THIS nurse. A status chip alone
                // would leave the patient parked here indefinitely — nothing
                // would tell the nurse it is now their move.
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<ExitToAppIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setDischargeTrackerOpen(true)}
                >
                  Complete Discharge
                </Button>
              )}
              {dischargeProcess === null && !isDischargeCancelled && (
                <Tooltip title={hasDischargeAdvice === true ? "Start the discharge process" : "Awaiting doctor's discharge advice."}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ExitToAppIcon sx={{ fontSize: 16 }} />}
                      disabled={hasDischargeAdvice !== true}
                      onClick={() => setDischargeModalOpen(true)}
                    >
                      Initiate Discharge
                    </Button>
                  </span>
                </Tooltip>
              )}
              {isDischargeCancelled && (
                <Box sx={{ width: "100%", mt: 1 }}>
                  <Tooltip title={effectiveCancellation?.remarks ? `Doctor cancelled discharge: ${effectiveCancellation.remarks}` : "Doctor cancelled the discharge"}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<ExitToAppIcon sx={{ fontSize: 16 }} />}
                        disabled={true}
                      >
                        Discharge Cancelled
                      </Button>
                    </span>
                  </Tooltip>
                  {effectiveCancellation?.remarks && (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: "error.main", mt: 0.5, fontWeight: 500 }}
                    >
                      Doctor reason: {effectiveCancellation.remarks}
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {dischargeProcess?.outcome === "deceased" && <DeathRecordPanel process={dischargeProcess} />}

      {doctorInstructions !== undefined && <DoctorInstructionsPanel notes={doctorInstructions} />}

      <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: `1px solid ${NURSE_COLORS.border}`, px: 1 }}>
          <Tab label="Vitals & I/O" /><Tab label="Medications" /><Tab label="Tasks" /><Tab label="Nursing Notes" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 0 && <VitalsTab admissionId={admissionId} patientId={patientId} dateFrom={dateFrom} dateTo={dateTo} />}
          {tab === 1 && <MedicationsTab admissionId={admissionId} patientId={patientId} />}
          {tab === 2 && <TasksTab admissionId={admissionId} dateFrom={dateFrom} dateTo={dateTo} onOpenMedications={() => setTab(1)} />}
          {tab === 3 && <NotesTab admissionId={admissionId} patientId={patientId} dateFrom={dateFrom} dateTo={dateTo} />}
        </Box>
      </Paper>

      <NursingAssessmentDialog
        open={assessmentDialog}
        onClose={() => setAssessmentDialog(false)}
        onSaved={loadClinical}
        admissionId={admissionId}
        patientId={patientId}
        latest={latestAssessment}
      />

      <Dialog
        open={dischargeTrackerOpen}
        onClose={() => setDischargeTrackerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Discharge Progress</DialogTitle>
        <DialogContent dividers>
          {/* actingAsNurse: this screen only ever renders for a nurse, and a
              nurse's Role.name is not "nurse" (they're NurseRecord-backed, and
              the role reads as "facility user"), so role-keyword matching would
              hide their own step from them. UX-only — the backend still checks
              DischargeStageRule + an active NurseBedAssignment. */}
          <DischargeStageTracker
            admissionId={admissionId}
            onAdvanced={loadDischargeProcess}
            actingAsNurse
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDischargeTrackerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <DischargeModal open={dischargeModalOpen} onClose={closeDischargeModal} admissionId={admissionId} />
      <NurseOrderModal
        open={placeOrderOpen}
        onClose={() => setPlaceOrderOpen(false)}
        admissionId={admissionId}
        careContextId={patient?.careContextRef || ""}
        defaultDoctorId={patient?.consultantId || ""}
        defaultDoctorName={patient?.consultant || ""}
      />
      <ReturnMedsDialog
        open={returnMedsOpen}
        onClose={() => setReturnMedsOpen(false)}
        admissionId={admissionId}
        patientId={patientId}
        patientName={patient?.name}
      />
    </Box>
  );
}
